import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from "rxjs";
import {CbRecordDocumentationApiService} from "../../service/api/cb-record-documentation-api.service";
import {CbConfigurationEntityDto} from "../../model/dto/api/cb-configuration-entity.dto";
import {CbRecordDocGenerateRequestDto} from "../../model/dto/api/cb-record-doc-generate-request.dto";
import {CbRecordDocGenerateResultDto} from "../../model/dto/api/cb-record-doc-generate-result.dto";
import {MvsMessageService} from "../../../../core/shared/service/message/mvs-message.service";
import {ConfirmationService} from "primeng/api";

/**
 * Summary statistics interface.
 */
interface SummaryStats {
    totalEntities: number;
    totalRecords: number;
    documentedRecords: number;
    overallCoveragePercentage: number;
    fullyDocumentedEntities: number;
    entitiesWithNoDocumentation: number;
}

/**
 * CB Record Documentation Component.
 * Allows users to select configuration entities and generate AI-powered documentation
 * for their records.
 */
@Component({
    selector: 'cb-record-documentation',
    templateUrl: './cb-record-documentation.component.html',
    styleUrls: ['./cb-record-documentation.component.scss'],
    standalone: false
})
export class CbRecordDocumentationComponent implements OnInit, OnDestroy {

    // Loading states
    loading: boolean = false;
    generating: boolean = false;

    // Data
    modules: string[] = [];
    entities: CbConfigurationEntityDto[] = [];
    filteredEntities: CbConfigurationEntityDto[] = [];

    // Selection
    selectedModule: string = '';
    selectedEntities: CbConfigurationEntityDto[] = [];

    // Summary stats
    summary: SummaryStats = {
        totalEntities: 0,
        totalRecords: 0,
        documentedRecords: 0,
        overallCoveragePercentage: 0,
        fullyDocumentedEntities: 0,
        entitiesWithNoDocumentation: 0
    };

    // Generation options
    overwriteExisting: boolean = false;
    includeTechnicalDescription: boolean = true;
    includeUsageNotes: boolean = true;
    additionalContext: string = '';
    maxRecordsPerEntity: number = 0;

    // Results
    lastResult: CbRecordDocGenerateResultDto | null = null;
    showResults: boolean = false;

    private subscriptions: Subscription[] = [];

    constructor(
        private recordDocApiService: CbRecordDocumentationApiService,
        private messageService: MvsMessageService,
        private confirmationService: ConfirmationService
    ) {
    }

    ngOnInit(): void {
        this.loadData();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    /**
     * Load all data.
     */
    loadData(): void {
        this.loading = true;

        // Load modules and entities in parallel
        this.recordDocApiService.getModules().subscribe({
            next: (modules) => {
                this.modules = modules;
            },
            error: (error) => {
                console.error('Error loading modules:', error);
            }
        });

        this.recordDocApiService.getConfigurationEntities().subscribe({
            next: (entities) => {
                this.entities = entities;
                this.filteredEntities = entities;
                this.loading = false;
            },
            error: (error) => {
                console.error('Error loading entities:', error);
                this.loading = false;
            }
        });

        this.recordDocApiService.getSummary().subscribe({
            next: (summary) => {
                this.summary = summary;
            },
            error: (error) => {
                console.error('Error loading summary:', error);
            }
        });
    }

    /**
     * Filter entities by module.
     */
    onModuleChange(): void {
        if (this.selectedModule) {
            this.filteredEntities = this.entities.filter(e => e.moduleName === this.selectedModule);
        } else {
            this.filteredEntities = this.entities;
        }
        // Clear selection when filter changes
        this.selectedEntities = [];
    }

    /**
     * Select all filtered entities.
     */
    selectAll(): void {
        this.selectedEntities = [...this.filteredEntities];
    }

    /**
     * Clear selection.
     */
    clearSelection(): void {
        this.selectedEntities = [];
    }

    /**
     * Get number of selected records.
     */
    get selectedRecordCount(): number {
        return this.selectedEntities.reduce((sum, e) => sum + e.totalRecords, 0);
    }

    /**
     * Get coverage badge severity.
     */
    getCoverageSeverity(percentage: number): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
        if (percentage >= 100) return 'success';
        if (percentage >= 75) return 'info';
        if (percentage >= 50) return 'warn';
        if (percentage > 0) return 'danger';
        return 'secondary';
    }

    /**
     * Start documentation generation.
     */
    generateDocumentation(): void {
        if (this.selectedEntities.length === 0) {
            this.messageService.showErrorMessage('Error', 'Please select at least one entity');
            return;
        }

        const totalRecords = this.selectedRecordCount;
        const message = `Generate documentation for ${this.selectedEntities.length} entities with ${totalRecords} records? This may take a while.`;

        this.confirmationService.confirm({
            message: message,
            header: 'Generate Documentation',
            icon: 'fa-regular fa-robot',
            accept: () => {
                this.executeGeneration();
            }
        });
    }

    /**
     * Execute the documentation generation.
     */
    private executeGeneration(): void {
        this.generating = true;
        this.showResults = false;

        const request: CbRecordDocGenerateRequestDto = {
            objectTypeAliases: this.selectedEntities.map(e => e.objectTypeAlias),
            overwriteExisting: this.overwriteExisting,
            includeTechnicalDescription: this.includeTechnicalDescription,
            includeUsageNotes: this.includeUsageNotes,
            additionalContext: this.additionalContext || undefined,
            maxRecordsPerEntity: this.maxRecordsPerEntity || undefined
        };

        this.recordDocApiService.generateDocumentation(request).subscribe({
            next: (result) => {
                this.lastResult = result;
                this.showResults = true;
                this.generating = false;

                // Refresh data
                this.loadData();

                if (result.failed > 0) {
                    this.messageService.showWarningMessage(
                        'Completed with Errors',
                        `Generated ${result.successfullyDocumented} docs, ${result.failed} failed`
                    );
                } else {
                    this.messageService.showSuccessMessage(
                        'Generation Complete',
                        `Successfully documented ${result.successfullyDocumented} records`
                    );
                }
            },
            error: (error) => {
                console.error('Error generating documentation:', error);
                this.generating = false;
                this.messageService.showErrorMessage('Error', 'Failed to generate documentation');
            }
        });
    }

    /**
     * Generate documentation for a single entity.
     */
    generateForEntity(entity: CbConfigurationEntityDto): void {
        this.confirmationService.confirm({
            message: `Generate documentation for ${entity.entityName} (${entity.totalRecords} records)?`,
            header: 'Generate Documentation',
            icon: 'fa-regular fa-robot',
            accept: () => {
                this.generating = true;

                this.recordDocApiService.generateForEntity(
                    entity.objectTypeAlias,
                    this.overwriteExisting,
                    this.additionalContext
                ).subscribe({
                    next: (result) => {
                        this.lastResult = result;
                        this.showResults = true;
                        this.generating = false;
                        this.loadData();

                        this.messageService.showSuccessMessage(
                            'Generation Complete',
                            `Documented ${result.successfullyDocumented} records for ${entity.entityName}`
                        );
                    },
                    error: (error) => {
                        console.error('Error generating documentation:', error);
                        this.generating = false;
                        this.messageService.showErrorMessage('Error', 'Failed to generate documentation');
                    }
                });
            }
        });
    }

    /**
     * Close results panel.
     */
    closeResults(): void {
        this.showResults = false;
        this.lastResult = null;
    }

    /**
     * Refresh data.
     */
    refresh(): void {
        this.loadData();
    }

    /**
     * Format coverage percentage.
     */
    formatCoverage(percentage: number): string {
        return percentage.toFixed(1) + '%';
    }
}
